/*
  Copy for the marketing page, kept out of the component so it can be edited
  without touching layout. Wording is carried over from the printed brochure so
  the two stay in step — including the corrections made there:

  - the product assists allocation, it does not allocate automatically
  - clients have no login today, so nothing here promises client access

  Register: the reader is a working owner or manager, not a software buyer.
  Plain words, short sentences, and the thing itself rather than a phrase about
  it. "Step" not "stage", "stock in hand" not "inventory ledger", "who is free"
  not "capacity". If a line sounds clever, it is probably too clever — say what
  the screen actually does.
*/

export const PRODUCTS = [
  {
    id: "dms",
    name: "Project & Document Management",
    tag: "For client work",
    who: "Agencies, consultancies and service businesses",
    body: "An all-in-one alternative to Asana and Jira: tasks, approvals and boards, plus the workflows, projects, schedules and reports around them.",
  },
  {
    id: "manufacturing",
    name: "Manufacturing & Production Tracking",
    tag: "For the shop floor",
    who: "Factories and job shops",
    body: "For orders that move through the shop floor. Production, stock, daily checks and dispatch in one place, with updates over WhatsApp.",
  },
];

/*
  The hero carries one screenshot but the headline promises two products, so it
  is switchable and labelled. A single dashboard under "every job, every step"
  reads as though the manufacturing half does not exist.
*/
export const HERO_SHOTS = [
  {
    id: "dms",
    label: "Project & Document Management",
    image: "/site/dashboard.webp",
    alt: "The DecisionFlow project dashboard showing work finished on time, work sent back for changes, how busy the team is and average days per job",
    caption:
      "The main dashboard — how much work finished on time, how much came back for changes, and how busy the team is.",
  },
  {
    id: "manufacturing",
    label: "Manufacturing & Production Tracking",
    image: "/site/mfg-dashboard.webp",
    alt: "The DecisionFlow manufacturing dashboard showing the live order tracker",
    caption:
      "The order tracker — every order, the step it is on now, and how many parts are still left to make.",
  },
];

/*
  Mixed on purpose: half of these have to land for a factory owner, or the
  section reads as an agency page and the manufacturing half looks bolted on.
*/
export const SYMPTOMS = [
  {
    title: "Everything lives in a spreadsheet",
    body: "Someone has to remember to update the file, and by Friday nobody believes what it says.",
  },
  {
    title: "Nobody knows who is free",
    body: "Work goes to whoever picks up first, not to the person who actually has time this week.",
  },
  {
    title: "You hear about delays late",
    body: "A job sits untouched for four days, and you find out when the customer calls to ask.",
  },
  {
    title: "Repeat work goes unrecorded",
    body: "A job is done again three times and nothing is written down, so it happens again next month.",
  },
  {
    title: "The steps are only in people's heads",
    body: "How a job gets done depends on who is running it, and new staff learn by asking around.",
  },
  {
    title: "Reports take a whole evening",
    body: "Hours worked, jobs finished, days per step — all of it typed out by hand when someone asks.",
  },
  {
    title: "You promise a date and hope it holds",
    body: "Nobody checks whether the floor can actually make it before the order is confirmed, so the promise is a guess.",
  },
  {
    title: "A part runs out mid-order",
    body: "Stock in hand only gets counted when someone notices it is low — usually because the line has already stopped.",
  },
  {
    title: "Dispatch gets written up after the truck leaves",
    body: "Tracking and invoice numbers get typed in later, if at all, so nobody can say what actually shipped and when.",
  },
];

/*
  The project side is pitched as an all-in-one alternative to Asana and Jira:
  the task management those tools are bought for, plus workflows, projects and
  scheduling. Both halves get their own card, sub-heading and run of
  showcases. Worded as "what you'd use them for", never as a claim about what
  they lack — Jira in particular has its own issue workflows.
*/
export const DMS_PILLARS = [
  {
    id: "tasks",
    title: "Task management",
    tagline: "Everything you'd use Asana or Jira for.",
    body: "Tasks and smaller tasks inside them, owners and dates, lists and drag-and-drop boards, approvals, milestones and a thread on every task — with an inbox and a dashboard for each person.",
    points: ["My Tasks as a list or a board", "Approvals and milestones", "A shared board inside every project"],
  },
  {
    id: "workflows",
    title: "Workflows & projects",
    tagline: "And the rest of what your business runs on.",
    body: "Set up your steps once and every project follows them — who owns each step, how long it should take, and who checks it before the job moves on. Work is given out with leave and shifts in view.",
    points: ["Step templates with approvals", "Project tracker, step by step", "Planner, shifts and leave in one place"],
  },
];

export const WORKFLOW_SHOWCASE = [
  {
    tag: "Your process",
    title: "Set up your steps once. Use them on every project.",
    body: "Lay out the steps your work goes through on a simple drag-and-drop screen. Steps can run one after another, or side by side. For each one, choose who owns it, how many days it should take, and what has to be handed in before it can close. Every new project then follows the same steps instead of being worked out again.",
    points: [
      "Save as many step templates as you need",
      "Run two steps side by side and join them at an approval",
      "Files and approvals required at each step",
    ],
    image: "/site/workflow-builder.webp",
    alt: "The workflow builder screen showing two sets of steps running side by side and joining at an approval step",
  },
  {
    tag: "Projects",
    title: "See exactly where every project stands.",
    body: "Each project has a tracker showing what is finished, what is running right now, who is holding it, and what is waiting behind it. When a step is sent back for changes, that is recorded on the project instead of getting lost in a chat.",
    points: [
      "Step-by-step progress with owner and dates",
      "Changes and repeat work recorded, not forgotten",
      "Deadline, priority and urgent flag on every project",
    ],
    image: "/site/project-detail.webp",
    alt: "A project page showing the step tracker with two steps running side by side",
  },
  {
    tag: "Giving out work",
    title: "Hand out work knowing who is actually free.",
    body: "When you give a step to someone, DecisionFlow shows who can take it — going by their shift, their approved leave, holidays and the work they are already carrying. It also shows the finish date each choice would give you, before you confirm.",
    points: [
      "Leave, shifts and holidays taken into account",
      "Finish date shown as you pick a person",
      "Other options when nobody is fully free",
    ],
    image: "/site/allocation.webp",
    alt: "The work allocation screen showing who is available, their shift, and the expected finish date",
  },
  {
    tag: "Shifts and leave",
    title: "One place for shifts, leave and holidays.",
    body: "Set the working days and hours for the company, then change them for individual people. Mark leave, half-days and public holidays on one calendar. Everything after that — giving out work, planning, attendance — follows it on its own.",
    points: [
      "Shift pattern per person, set day by day",
      "Leave, half-days and public holidays on one screen",
      "Used by work allocation, planning and attendance",
    ],
    image: "/site/roster.webp",
    alt: "The staff schedule screen showing each person's shift day by day with leave and holidays marked",
  },
];

export const TASK_SHOWCASE = [
  {
    // Same claims as the brochure's Task Management page, in the site's plainer
    // register. The inbox carries assignments and @mentions only.
    tag: "Tasks",
    title: "Every to-do and follow-up gets an owner and a date.",
    body: "Not everything fits into the steps of a project — a call to return, a quote to check, a favour a client asked for. Write it down as a task, give it to someone, set the date and link it to the project it belongs to. Talk it through on the task itself, and @mention anyone who needs to see it. They get it in their inbox, not in a chat thread that scrolls away.",
    points: [
      "A list or a drag-and-drop board, with your own sections",
      "Smaller tasks inside a task, and tasks that wait on others",
      "Time spent on tasks counts in the same reports as project work",
    ],
    image: "/site/tasks.webp",
    alt: "The My Tasks screen with a task open, showing its smaller tasks, time logged and comments",
  },
  {
    // Only the approver (or an admin) decides. Approve and reject close it,
    // "request changes" keeps it open, and the person who asked is told.
    tag: "Approvals and milestones",
    title: "Sign-offs that are written down, not remembered.",
    body: "Make any task an approval and give it to the person who has to say yes. It shows up in their inbox, and they answer with one click — approve, ask for changes, or reject. Asking for changes keeps it open so the work can be redone, and whoever asked for the approval is told the answer. Milestones mark the points a project has to reach, so everyone can see how close it is.",
    points: [
      "Approve, ask for changes or reject — with who decided and when",
      "Work that depends on an approval shows as waiting until it is given",
      "Milestones sit in the same lists and boards as everyday tasks",
    ],
    image: "/site/tasks-approval.webp",
    alt: "An approval task with Approve, Request changes and Reject buttons, waiting on the draft it depends on",
  },
  {
    tag: "Project boards",
    title: "Every project gets its own board.",
    body: "Inside each project, the team shares one set of columns — To do, In progress, In review, Done, or whatever your work actually goes through. Drag a task from one column to the next as it moves. Everyone on the project sees the same board, and the time logged against each column adds up as you go.",
    points: [
      "Your own columns for each project, shared by the whole team",
      "Switch between a list and a board",
      "Time logged shown on every column",
    ],
    image: "/site/project-boards.webp",
    alt: "A project's task board with To do, In progress, In review and Done columns",
  },
  {
    tag: "Your day at a glance",
    title: "Open the app and see what needs you.",
    body: "Each person gets a dashboard of their own tasks — what is late, what is due this week, which approvals are waiting on them, and what is held up by other work. Everything given to them or mentioning them lands in one inbox. On the home screen, managers see the same picture for the whole team.",
    points: [
      "Late, due today and due this week, counted for you",
      "Approvals waiting on you, and tasks waiting on others",
      "Team view by person and by project for managers",
    ],
    image: "/site/tasks-dashboard.webp",
    alt: "The task dashboard showing overdue tasks, tasks due soon, approvals waiting and time logged",
  },
];

export const FEATURES = [
  {
    title: "Planner and daily view",
    body: "A month calendar for the team and a daily list for each person, so everyone opens the app already knowing what they are working on.",
  },
  {
    title: "Alerts and figures",
    body: "How much work finished on time, how much came back for changes, how busy the team is and how many days a job takes — plus an alert when a step runs past its date.",
  },
  {
    title: "Roles and access",
    body: "Create as many roles as you need, and choose exactly what each one can see and do. Works for permanent staff and freelancers alike.",
  },
  {
    title: "Billing list",
    body: "Finished work moves into a billing list, so nothing you delivered goes unbilled.",
  },
  {
    title: "Set up your own way",
    body: "Add your own project fields, your own shift timings and your own status colours from the settings. No coding, no developer.",
  },
  {
    title: "Cloud or your own server",
    body: "Run it on our servers, or inside your own office network. Either way, setup takes under 30 minutes.",
  },
];

/*
  One client's numbers, unattributed — same figures and the same caveats as the
  brochure's Results page (see build-dms-brochure.js). "60%" is the best month
  since, not the average; the note under the cards says so.
*/
export const RESULTS = [
  { figure: "2×", label: "faster at each step", body: "Time per step fell from over 80 hours to 30–40 hours." },
  { figure: "23% → 6%", label: "work finished in overtime", body: "From nearly a quarter of all steps to about one in seventeen." },
  { figure: "40% → 60%", label: "projects delivered on time", body: "From around 40% in the first months to 60% in the best month since." },
  { figure: "+47%", label: "more people using it", body: "From 17 people to 25, as more of the team moved their work in." },
];

export const REPORTS = [
  {
    title: "Project Completion Report",
    body: "The full story of one project — every step, who worked on it, hours spent, changes raised, from brief to final delivery.",
  },
  {
    title: "Staff Activity Report",
    body: "What one person did over any dates: steps handled, hours worked, days active, and a simple chart.",
  },
  {
    title: "All Projects Report",
    body: "Every project in a date range with its status, its owner and whether it was delivered on time. The one to open before a review meeting.",
  },
  {
    title: "Team Attendance Report",
    body: "An attendance chart that already knows each person's shift, their approved leave and public holidays.",
  },
];

export const STEPS = [
  "A new job comes in. You create the project, pick the steps it follows, set a deadline and mark it urgent if it is.",
  "You give out each step, and the system shows you who is free once leave, shifts and holidays are counted.",
  "Each person sees the work on their daily list and starts when they are ready.",
  "Files are handed in step by step, and each step is checked before the job moves on.",
  "Once all the steps are done, the project is marked delivered.",
  "A full report is ready — time spent, changes made, and the whole story from brief to delivery.",
];

/* ------------------------------------------------------------ manufacturing */

/*
  The four things the module actually does. Led with as a band, because
  "production tracking" alone undersells it — stock and the daily checks are
  peers of the order tracker, not extras hanging off it.
*/
export const MFG_PILLARS = [
  {
    title: "Production",
    body: "Check whether an order can be delivered on time, then follow it step by step as parts move through the floor.",
  },
  {
    title: "Stock",
    body: "Stock in hand for every part, a record of everything received and issued, low-stock alerts and supplier details.",
  },
  {
    title: "Daily checks",
    body: "The routine jobs a plant runs on — asked of the right person, at the right time, answered on Telegram.",
  },
  {
    title: "Dispatch and reports",
    body: "Shipments with tracking and invoice numbers, delivery confirmed, and a production report every day.",
  },
];

export const MFG_SHOWCASE = [
  {
    tag: "New orders",
    title: "Know if you can meet the delivery date before you accept the order.",
    body: "Enter the part, the quantity and the date the customer wants. DecisionFlow works out how long the job needs from that part's own production rate — how many days a batch of that size takes — and shows you the days required, the spare days left, and the date work has to start. All while the order is still a question, not a promise.",
    points: [
      "Days needed worked out from the part and the quantity",
      "Finish date and spare days shown before you save",
      "Orders that start later wait, then start on their own",
    ],
    image: "/site/mfg-create-po.webp",
    alt: "The new order screen showing the delivery date check with the expected finish date",
  },
  {
    tag: "Live tracking",
    title: "Parts move step to step. The board keeps up on its own.",
    body: "When someone reports parts finished at one step, those parts arrive at the next step, which starts and tells its operator. Every order carries its own status — waiting, running, at risk, late or finished — so the board tells you where the trouble is instead of you going to find it.",
    points: [
      "Finished parts move to the next step on their own",
      "At risk and late marked against the delivery date",
      "A full record of who reported what, and when",
    ],
    image: "/site/mfg-dashboard.webp",
    alt: "The manufacturing dashboard showing the live order tracker with step progress and status flags",
  },
  {
    tag: "WhatsApp and Telegram",
    title: "The shop floor reports progress without opening the app.",
    body: "Operators get a message on WhatsApp or Telegram when a step becomes theirs — the part, the quantity and the date it is needed. They reply with a number to report what they finished. Work sent back goes to the step they choose, dispatch confirms delivery with one tap, and supervisors get a daily summary and an alert when an order runs late.",
    points: [
      "Replying “40” records 40 parts finished at that step",
      "Send work back to an earlier step from the chat itself",
      "Daily summary, late-order and low-stock messages",
    ],
    image: "/site/mfg-my-tasks.webp",
    alt: "The my tasks screen where an operator checks in and reports the parts they finished",
  },
  {
    tag: "Daily checks",
    title: "The routine checks that ask for themselves.",
    body: "Machine checks, cleaning rounds, stock counts — the jobs that matter every day and quietly get missed. Give one to a person or a group, choose when it should be asked, and DecisionFlow puts the question to them on Telegram at that time and saves what they answered.",
    points: [
      "Daily, weekly or monthly, at a time you choose",
      "Yes/no or a written answer — and a “no” can ask for a reason",
      "Reminders until it is done, with misses counted per person",
    ],
    image: "/site/mfg-allotted-tasks.webp",
    alt: "The daily checks screen listing routine check-ins and each person's answers",
  },
  {
    tag: "Stock",
    title: "Stock figures you can trust, without a second spreadsheet.",
    body: "Every part carries its stock in hand, its minimum and maximum level, how much to reorder, its cost per unit, where it is stored and who supplies it. Anything below its minimum is marked on the list and sent out as an alert, and one message every day tells you what to order before it stops the line.",
    points: [
      "Low stock and out of stock marked on the part itself",
      "Categories, storage locations and suppliers built in",
      "A low-stock message every day, not a report someone must remember to run",
    ],
    image: "/site/mfg-inventory.webp",
    alt: "The stock list showing stock in hand, minimum levels, storage locations and low-stock flags for each part",
  },
  {
    tag: "Stock in and out",
    title: "Every item in and out, written down.",
    body: "Every receipt, issue, scrap and return is saved with its quantity, the reason, a reference number and the person who entered it. When the stock on the shelf and the stock on the screen do not match, the answer is in the history instead of in somebody's memory.",
    points: [
      "One record of everything in and out, across all parts",
      "Reason and reference number saved on every entry",
      "Filter by part, type or date, and download as a spreadsheet",
    ],
    image: "/site/mfg-movements.webp",
    alt: "The stock in and out screen showing entries with their reasons and reference numbers",
  },
  {
    tag: "Dispatch",
    title: "The order is not finished until it reaches the customer.",
    body: "Dispatch records each shipment with its tracking number, invoice number, mode of transport and quantity — an order often goes out in more than one lot. Delivery is confirmed against the shipment, and only then does the order close, with its whole history saved behind it.",
    points: [
      "More than one shipment per order, each tracked on its own",
      "Delivery confirmed by the person who sent it",
      "Full record from raw material to delivery",
    ],
    image: "/site/mfg-job-detail.webp",
    alt: "A finished order showing its step table, its history and its dispatch record",
  },
];

export const MFG_FEATURES = [
  {
    title: "Parts and their steps",
    body: "Enter each part once — its code, its production rate, its supplier — and the steps it goes through. Production, quality check, raw material and dispatch steps each work differently, because they are different jobs.",
  },
  {
    title: "Raw material reminders",
    body: "Link a material order to the step that uses it, and the reminder to order the next lot comes when production reaches that step.",
  },
  {
    title: "Daily production report",
    body: "Steps finished today, total parts moved and every late order — on one page, with a date picker, ready to download.",
  },
  {
    title: "Orders that start later",
    body: "An order booked for next month waits, then starts itself on the morning it has to begin and tells the first operator.",
  },
  {
    title: "Roles and access",
    body: "Operators see their own steps and nothing else. Supervisors see the whole floor. You decide where the line sits.",
  },
  {
    title: "One system",
    body: "Same login, same settings, same billing as the project side. You pick the version that fits your business when you sign up.",
  },
];

export const MFG_STEPS = [
  "A customer order comes in — you enter it with the part, the quantity and the delivery date.",
  "The system adds up the time every step needs and tells you whether that date can be met.",
  "If the job starts later, the order waits, then starts itself on the right morning.",
  "The first operator gets a WhatsApp or Telegram message with the part, the quantity and the date.",
  "Operators reply with what they finished — those parts move to the next step, and its operator is told.",
  "Dispatch records the shipment, delivery is confirmed, and the order closes with its full history saved.",
];

export const COMPARISON = {
  columns: ["A spreadsheet", "Asana, Jira and similar", "DecisionFlow"],
  rows: [
    {
      label: "Your way of working",
      cells: [
        "Only in people's heads",
        "Boards and statuses you set up per team",
        "Set up once, used on every job",
      ],
    },
    {
      label: "Everyday to-dos and follow-ups",
      cells: [
        "Rows nobody updates",
        "What it does best",
        "Built in, and linked to the project",
      ],
    },
    {
      label: "Who takes the next step",
      cells: [
        "Whoever is asked",
        "You pick by hand",
        "Picked with leave, shifts and holidays in view",
      ],
    },
    {
      label: "Shifts, leave and holidays",
      cells: ["A separate sheet", "Not included", "Built in and followed everywhere"],
    },
    {
      label: "Changes and repeat work",
      cells: ["Not tracked", "A card reopened", "Recorded on the step and on the job"],
    },
    {
      label: "Reports",
      cells: ["An evening of typing", "Needs setting up first", "Four reports, ready to download"],
    },
    {
      label: "Checking a delivery date before you accept",
      cells: ["A guess, or a call to the floor", "Not something it does", "Worked out from the part's own production rate"],
    },
    {
      label: "Stock in hand",
      cells: ["A separate sheet, out of date by Friday", "Not included", "Live figures with low-stock alerts sent out"],
    },
    {
      label: "Dispatch and delivery",
      cells: ["A notebook, or not tracked at all", "Not included", "Shipments, tracking and invoice numbers, delivery confirmed"],
    },
    {
      label: "Built for",
      cells: ["Nothing in particular", "General task tracking and software teams", "Teams and factories that deliver to customers"],
    },
  ],
  // The column the table is arguing for — highlighted in the UI.
  highlight: 2,
};
